import { CartLinesUpdateEvent } from '@shopify/events';

/**
 * Cart page enhancement.
 *
 * The page is a real `<form action="/cart" method="post">` with `updates[]` inputs and a
 * named submit, so quantity changes and removals work with JavaScript disabled — the
 * browser posts and Shopify re-renders the page. Everything here is an upgrade:
 * changing a quantity updates in place via /cart/change.js instead of a reload.
 *
 * Because the markup is a working form first, a failure here degrades to "press Update
 * cart", never to a dead control.
 */
(() => {
  const money = (cents, sample) => {
    // Reuse the currency symbol already rendered by Liquid rather than guessing a locale.
    const symbol = (sample.match(/^[^\d]*/) || [''])[0];
    return `${symbol}${(cents / 100).toFixed(2)}`;
  };

  const init = (form) => {
    if (form.dataset.ebCartBound === 'true') return;
    form.dataset.ebCartBound = 'true';

    const subtotal = document.querySelector('[data-eb-cart-subtotal]');
    const updateBtn = form.querySelector('[data-eb-cart-update]');

    const change = async (key, quantity, line) => {
      form.setAttribute('aria-busy', 'true');
      try {
        const res = await fetch(`${Theme.routes.cart_change_url}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: key, quantity }),
        });
        if (!res.ok) throw new Error('change failed');
        const cart = await res.json();

        if (subtotal) subtotal.textContent = money(cart.total_price, subtotal.textContent);

        // Removing the last of a line, or emptying the cart, changes the page structure —
        // let Shopify re-render rather than trying to patch the DOM.
        if (quantity === 0 || cart.item_count === 0) {
          window.location.reload();
          return;
        }

        const item = cart.items.find((i) => i.key === key);
        if (item && line) {
          const lineTotal = line.querySelector('.eb-cart__line-total');
          if (lineTotal) lineTotal.textContent = money(item.original_line_price, lineTotal.textContent);
        }

        document.dispatchEvent(
          new CartLinesUpdateEvent({ action: 'update', context: 'cart', lines: [] })
        );
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      } catch {
        // Fall back to the native form path.
        if (updateBtn) updateBtn.hidden = false;
      } finally {
        form.removeAttribute('aria-busy');
      }
    };

    form.addEventListener('click', (event) => {
      const step = event.target.closest('[data-eb-cart-qty]');
      if (step) {
        const line = step.closest('[data-eb-cart-line]');
        const input = line?.querySelector('[data-eb-cart-qty-input]');
        if (!input) return;
        const next = Math.max(0, (parseInt(input.value, 10) || 0) + Number(step.dataset.ebCartQty));
        input.value = String(next);
        change(input.dataset.key, next, line);
        return;
      }

      const remove = event.target.closest('[data-eb-cart-remove]');
      if (remove) {
        event.preventDefault();
        const line = remove.closest('[data-eb-cart-line]');
        change(remove.dataset.key, 0, line);
      }
    });

    form.addEventListener('change', (event) => {
      const input = event.target.closest('[data-eb-cart-qty-input]');
      if (!input) return;
      const qty = Math.max(0, parseInt(input.value, 10) || 0);
      input.value = String(qty);
      change(input.dataset.key, qty, input.closest('[data-eb-cart-line]'));
    });

    // With JS working, the manual Update button is redundant.
    if (updateBtn) updateBtn.hidden = true;
  };

  const boot = () => {
    for (const form of document.querySelectorAll('[data-eb-cart-form]')) init(form);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', boot);
})();
